// `listForecasts` returns `{ok, forecasts, pinned}`; `ok:false` on a non-2xx OR a null body,
// so a bad response fails the load instead of silently hydrating zero rows.
import { hydrateRows, rowRunId } from "../lib/forecast/hydrate.js";
import { hydrated, detailHydrated } from "../lib/forecast/reducer.js";
import { recordUiEvent } from "../lib/logRingBuffer";
import { DETAIL_FIELDS } from "../lib/forecast/model.js";
import { apiGet } from "../lib/apiClient.js";
import { makeTtlCache } from "../lib/forecast/reportCache.js";
import { applyNewestReforecast } from "../lib/forecast/reforecastPickup.js";
import { mergeReportPointers } from "../lib/forecast/reportPointerMerge.js";
import { selectRun } from "../lib/forecast/selectors.js";
import { sharedLiveVintages } from "../lib/forecast/sharedLiveVintages.js";
import { reportFailure } from "../lib/failureCounts.js";
import type { CommandDeps } from "./deps.js";

// Per-tab TTL cache for the post-load "promote newest reforecast" overlay, keyed by
// row_key — same key space/TTL convention as the other report readers, but its own Map:
// this overlay fires once per row on load, not on scrub, so sharing isn't worth coupling.
const REPORT_TTL_MS = 5 * 60 * 1000;
const _newestReforecastCache = makeTtlCache({ ttlMs: REPORT_TTL_MS });

/**
 * Best-effort "newest reforecast becomes live" overlay: promote the newest async fire (if
 * strictly newer than the run's live/manual moment) into the run's current forecast. Each
 * row is its OWN fire-and-forget task (never awaited, so it doesn't gate the caller's
 * hydration-done, and a hung/failed row can't starve the others). It runs after the hydrate
 * dispatch so applyNewestReforecast finds the run in the store, and it re-checks the LIVE
 * run (via getRunState) right before dispatching, so a user action landing between load
 * and dispatch is never clobbered.
 *
 * MUST be given FULL rows. applyNewestReforecast derives the run's live moment from the
 * LAST `history` entry's `snapshotAt`, falling back to `createdAt` when history is empty
 * — and a lean row's history is `[]` (rowToWire coerces the absent column), which is
 * indistinguishable from "never manually reforecasted". Run against lean rows it would
 * therefore compare async fires to `createdAt` instead of the true live moment and
 * promote a STALE reforecast over the current one, on every reload.
 */
export function applyNewestReforecastAll(rows: any[], cancelled?: () => boolean): void {
  for (const row of rows) {
    // `fetchForecastReport` folds a `!ok` answer to the same null as "nothing newer", and
    // `apiGet` never throws, so the answer is observed on the way through: a report request
    // the server refused is a lost overlay, logged and counted like a rejection. A cache hit
    // makes no request and stays quiet.
    let answer: { ok: boolean; status: number } | undefined;
    const observed = async (...args: Parameters<typeof apiGet>) => {
      answer = await apiGet(...args);
      return answer;
    };
    void applyNewestReforecast(row as any, {
      apiGet: observed as any,
      cache: _newestReforecastCache,
      // Each row fetches its report and THEN dispatches. Guarding only the callers
      // above leaves that dispatch unguarded, so a task this boot started could land
      // `runReforecasted` in a store a remount has already reset — for the same run
      // id, overwriting the new session's row.
      cancelled,
    })
      .then(() => {
        if (answer && !answer.ok) {
          reportFailure(
            "forecasts.newest_reforecast_failed",
            new Error(`GET /api/forecast/report answered status ${answer.status}`),
          );
        }
      })
      .catch((err: unknown) => reportFailure("forecasts.newest_reforecast_failed", err));
  }
}

/** Cancellation ref shared with the boot sequence; its `current` flag is flipped on unmount. */
type CancelRef = { current: boolean } | undefined;

/**
 * The moment of the last saved vintage the server has delivered for each run (a detail fill or a
 * refresh), in ms. The run's own history can end earlier: after a resolution the reducer steps a
 * run back past vintages generated later (settledRun), so its history alone would make every
 * later list read look newer than what the tab already has.
 */
const deliveredVintageMs = new Map<string, number>();

/** Record the last saved vintage of `rows` (full rows) as delivered to their runs. */
export function noteDeliveredVintages(rows: any[]): void {
  for (const row of rows) {
    const runId = rowRunId(row);
    const history = row?.history;
    const last = Array.isArray(history) && history.length ? history[history.length - 1] : null;
    const ms = Date.parse(last?.snapshotAt ?? "");
    if (!runId || !Number.isFinite(ms)) continue;
    deliveredVintageMs.set(runId, Math.max(ms, deliveredVintageMs.get(runId) ?? -Infinity));
  }
}

/** The last saved vintage delivered for `runId`, in ms (NaN when none was). */
export function deliveredVintage(runId: string): number {
  return deliveredVintageMs.get(runId) ?? NaN;
}

/** Test-only reset. */
export function resetDeliveredVintages(): void {
  deliveredVintageMs.clear();
}

/** Each full row's detail keys, by run id: the DETAIL_HYDRATED payload. */
export function detailPatches(rows: any[]): Record<string, any> {
  const detailByRunId: Record<string, any> = {};
  for (const row of rows) {
    const runId = rowRunId(row);
    if (!runId) continue;
    const patch: Record<string, unknown> = {};
    for (const k of DETAIL_FIELDS) patch[k] = row[k] ?? null;
    // The headline comes from the same snapshot as the contract and history: the owner may have
    // saved a newer answer since the lean list was read. A local completion keeps its own
    // (DETAIL_HYDRATED). seriesImpacts/resolution/move ride along in the lean projection already,
    // so they are deliberately NOT re-patched — the live value may be newer than the one this
    // response was serialized from.
    if (row.serverPrediction !== undefined) patch.serverPrediction = row.serverPrediction;
    detailByRunId[runId] = patch;
  }
  return detailByRunId;
}

/**
 * Fill the lean rows among `rows` (full rows) with their detail, and promote each filled row's
 * newest reforecast. Only a run still `detailPending` is patched: detail fills a lean row once,
 * so a later, possibly older response (a second read of the same forecast, say) never overwrites
 * what an earlier one filled. Returns the rows it filled.
 */
export function fillLeanDetail(deps: CommandDeps, rows: any[], cancelled?: () => boolean): any[] {
  const state = deps.store.getState();
  const lean = rows.filter((row) => {
    const runId = rowRunId(row);
    return !!runId && selectRun(state, runId)?.detailPending === true;
  });
  if (!lean.length) return lean;
  deps.store.dispatch(detailHydrated(detailPatches(lean)) as any);
  noteDeliveredVintages(lean);
  // Promote from every pointer known now, not only the answer's: a realtime hint or a
  // reconnect may have merged a newer one into the run while this read was in flight, and once
  // filled the run is no longer read for one (loadForecastDetail hasNewerForecast). A shared
  // run is promoted only while shared live vintages are on.
  const filled = deps.store.getState();
  const promotable = lean.filter(
    (row) => filled.runsById[rowRunId(row) as string] || sharedLiveVintages(),
  );
  applyNewestReforecastAll(
    promotable.map((row) => {
      const live = selectRun(filled, rowRunId(row) as string)?.reportPointers;
      return { ...row, reportPointers: mergeReportPointers(live, row.reportPointers) };
    }),
    cancelled,
  );
  return lean;
}

export interface LoadForecastsResult {
  ok: boolean;
  pinned: Record<string, any>;
}

export async function loadForecasts(
  deps: CommandDeps,
  cancelledRef?: CancelRef,
): Promise<LoadForecastsResult> {
  const { store } = deps;
  // Boot on the LEAN projection: reload gates first meaningful render on the response, and
  // lean carries everything the list, the badges, the branch topology and the resume sweep
  // read. A forecast's detail is read by id once it is on screen, or once it has a newer
  // forecast to promote (commands/loadForecastDetail.ts), never as one whole-list download.
  const cancelled = () => cancelledRef?.current === true;
  const r = await deps.services.api.listForecasts({ view: "lean" });
  // On failure the caller still marks hydration finished so the one-shot compare hydration
  // isn't wedged, though nothing is hydrated.
  if (!r.ok) return { ok: false, pinned: {} };
  // Unmount DURING the lean request. `bootForecasts` checks cancellation only after this
  // returns, so without this the hydrate below still fires and HYDRATED merges this boot's
  // rows into a store the remount has already reset. `ok:false` is the honest report —
  // nothing was hydrated — and boot returns immediately on cancellation anyway.
  if (cancelled()) return { ok: false, pinned: {} };
  // Heal orphan placeholder branches persisted earlier. The wire is FLAT peer rows — branch
  // rows carry parentId != null. Resumable in-flight branches (traceId) are kept; they
  // rehydrate + reattach via the resume sweep.
  const _isPendingStatus = (s: any) => s === "running" || s === "preprocessing";
  const fcs = r.forecasts.filter(
    (f: any) => !(f.parentId != null && _isPendingStatus(f.status) && !f.traceId),
  );
  // Diagnostic: the forecasts.loaded event shows whether the server returned branches on
  // reload. It is captured in bug reports.
  const _branchRows = fcs.filter((f: any) => f.parentId != null);
  recordUiEvent({
    event: "forecasts.loaded",
    n: fcs.length,
    nBranches: _branchRows.length,
    nRunningBranches: _branchRows.filter((b: any) => b.status === "running").length,
  });
  // hydrateRows is called WITHOUT messagesByForecastId, so conversations hydrate empty here;
  // per-forecast messages load on demand via loadMessages → entriesLoaded.
  // hydrateRows lives in a still-JS module; its inferred empty-object return doesn't satisfy
  // the typed `hydrated` payload, so the result is cast at the boundary.
  store.dispatch(hydrated(hydrateRows(fcs, {}, { lean: r.lean }) as any));
  // A full (non-lean) answer carries its detail: promote each row's newest forecast now. Lean
  // rows are promoted when their detail is read (fillLeanDetail).
  if (!r.lean) applyNewestReforecastAll(fcs, cancelled);
  return { ok: true, pinned: r.pinned };
}
